"use client";

import { ChevronLeft } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import Link from "next/link";

interface SectionHeaderLCSettings {
    label: string | null;
    title: string;
    subtitle: string | null;
    show_accent: boolean;
    align: 'right' | 'center';
    link_text: string | null;
    link_url: string | null;
}

export default function SectionHeaderLC({ content }: {
    content: {
        settings: SectionHeaderLCSettings;
        data: null;
    };
}) {
    const { settings } = content;
    if (!settings.title) return null;

    if (settings.align === 'center') {
        return (
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                px: { xs: 2, md: 4 },
                py: { xs: 2, md: 3 },
            }}>
                <Typography component="h2" sx={{ fontWeight: 700, fontSize: { xs: 20, md: 24 }, color: '#242424', m: 0 }}>
                    {settings.title}
                </Typography>
                {settings.subtitle && (
                    <Typography sx={{ fontSize: { xs: 13, md: 14 }, color: 'text.secondary', mt: 0.5 }}>
                        {settings.subtitle}
                    </Typography>
                )}
                {settings.show_accent && (
                    <Box sx={{ width: 40, height: 3, bgcolor: '#BB202E', mt: 1.5, borderRadius: 0.5 }} />
                )}
                {settings.link_text && settings.link_url && (
                    <Box
                        component={Link}
                        href={settings.link_url}
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#BB202E', textDecoration: 'none', fontSize: 14, fontWeight: 500, mt: 1.5 }}
                    >
                        <span>{settings.link_text}</span>
                        <ChevronLeft sx={{ fontSize: 18 }} />
                    </Box>
                )}
            </Box>
        );
    }

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            pt: { xs: 2, md: 3 },
        }}>
            {/* In RTL flex row, first DOM element appears on the RIGHT side */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ...(settings.link_text && settings.link_url ? { flex: 1, minWidth: 0 } : { width: '100%' }) }}>
                {settings.show_accent && (
                    <Box sx={{ width: 5, height: 36, bgcolor: '#BB202E', borderRadius: 1, flexShrink: 0 }} />
                )}
                <Typography component="h2" sx={{ fontWeight: 700, fontSize: { xs: 17, md: 24 }, color: '#242424', m: 0 }}>
                    {settings.title}
                </Typography>
            </Box>
            {settings.link_text && settings.link_url && (
                <Box
                    component={Link}
                    href={settings.link_url}
                    sx={{
                        display: 'flex', alignItems: 'center', gap: 0.5, color: '#BB202E',
                        textDecoration: 'none',
                        fontSize: 14, fontWeight: 500, flexShrink: 0,
                    }}
                >
                    <span>{settings.link_text}</span>
                    <ChevronLeft sx={{ fontSize: 18 }} />
                </Box>
            )}
            {settings.subtitle && (
                <Typography sx={{
                    fontSize: { xs: 12, md: 13 },
                    color: 'text.secondary',
                    mt: 0,
                    width: '100%',
                    pl: settings.show_accent ? 'calc(5px + 12px)' : 0,
                }}>
                    {settings.subtitle}
                </Typography>
            )}
        </Box>
    );
}
